import React from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Divider,
  Chip,
  useTheme,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EmailIcon from '@mui/icons-material/Email'
import ScheduleIcon from '@mui/icons-material/Schedule'
import BusinessIcon from '@mui/icons-material/Business'
import { Link as RouterLink } from 'react-router-dom'
import { calculateGst } from '@/utils/billingConstants'

interface InvoicePaymentConfirmationProps {
  organisationName: string
  billingEmail: string
  tier?: number
  billingFrequency?: string
}

export default function InvoicePaymentConfirmation({
  organisationName,
  billingEmail,
  tier = 4,
  billingFrequency,
}: InvoicePaymentConfirmationProps) {
  const theme = useTheme()

  // Helper function to map billing frequency to invoice type label
  const getInvoiceTypeLabel = () => {
    if (!billingFrequency) return 'Invoice'

    const freq = billingFrequency.toLowerCase()
    if (freq === 'monthly') return 'Monthly Invoice'
    if (freq === 'quarterly') return 'Quarterly Invoice'
    if (freq === 'yearly' || freq === 'annual') return 'Annual Invoice'

    return 'Invoice'
  }

  // Pricing based on tier (ex-GST monthly rates)
  const tierPricing = {
    0: { monthly: 20, description: 'Tier Micro' },
    1: { monthly: 125, description: 'Tier 1 - Large Organisation' },
    2: { monthly: 125, description: 'Tier 2 - Medium Organisation' },
    3: { monthly: 50, description: 'Tier 3 - Small Organisation' },
    4: { monthly: 35, description: 'Tier 4 - Mini Organisation' },
  }

  // Default to tier 4 if no tier is set or tier is invalid
  const pricing =
    tierPricing[tier as keyof typeof tierPricing] || tierPricing[4]
  const monthlyPrice = pricing.monthly
  const gstAmount = calculateGst(monthlyPrice)
  const totalPrice = (monthlyPrice + gstAmount).toFixed(2)

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
      {/* Success Header */}
      <Box sx={{ mb: 4 }}>
        <CheckCircleIcon
          sx={{
            fontSize: 80,
            color: '#4caf50',
            mb: 2,
          }}
        />
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontWeight: 600, color: '#4caf50' }}
        >
          Account Activated!
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          Your subscription has been set up successfully
        </Typography>
        <Chip
          label="ACTIVE"
          color="success"
          variant="filled"
          sx={{ fontWeight: 600, fontSize: '0.8rem' }}
        />
      </Box>

      {/* Success Message */}
      <Alert
        severity="success"
        sx={{
          mb: 4,
          textAlign: 'left',
          '& .MuiAlert-message': { width: '100%' },
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
          Your account is now active and ready to use!
        </Typography>
        <Typography variant="body2">
          You now have unlimited access to all premium features including
          unlimited scans, matches, and applications.
        </Typography>
      </Alert>

      {/* Organisation Details */}
      <Paper sx={{ p: 3, mb: 4, textAlign: 'left' }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
        >
          <BusinessIcon color="primary" />
          Subscription Details
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Organisation:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {organisationName}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Plan:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {pricing.description}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Monthly Rate:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            ${totalPrice} (inc. GST)
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Payment Method:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {getInvoiceTypeLabel()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Status:
          </Typography>
          <Chip label="Active" color="success" size="small" />
        </Box>
      </Paper>

      {/* Next Steps */}
      <Paper sx={{ p: 3, mb: 4, textAlign: 'left' }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
        >
          <ScheduleIcon color="primary" />
          What Happens Next?
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <EmailIcon color="primary" sx={{ mt: 0.5 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Invoice Delivery
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your first invoice will be sent to <strong>{billingEmail}</strong>{' '}
              within the next hour. Subsequent invoices will be sent monthly on
              the same date.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <ScheduleIcon color="primary" sx={{ mt: 0.5 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Payment Terms
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Invoices are due within 30 days of the invoice date. Your account
              will remain active as long as payments are made on time.
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Need to update your billing information?</strong> You can
            manage your billing details and view past invoices from your account
            page at any time.
          </Typography>
        </Alert>
      </Paper>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Button
          disableRipple
          variant="contained"
          size="large"
          component={RouterLink}
          to="/match"
          sx={{ minWidth: 200 }}
        >
          Start Finding Funding
        </Button>

        <Button
          variant="outlined"
          disableRipple
          size="large"
          component={RouterLink}
          to="/account"
          sx={{
            minWidth: 150,
            '&:focus': {
              backgroundColor: '#e8e8e8',
            },
          }}
        >
          Manage Account
        </Button>
      </Box>

      {/* Contact Support */}
      <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
        <Typography variant="body2" color="text.secondary">
          Have questions about your subscription?{' '}
          <a
            href="/help"
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
            }}
          >
            Contact our support team
          </a>
        </Typography>
      </Box>
    </Box>
  )
}
